import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Trash2, MapPin, Pencil, X, Check, Search } from 'lucide-react';
import { DocumentTitle } from '../components/DocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { SavedSearch } from '../types';
import { getSavedSearches, deleteSavedSearch, updateSavedSearch, clearSavedSearches } from '../services/savedSearchesService';
import { Button } from '../src/components/ui/button';
import { IconButton } from '../src/components/ui/icon-button';
import SignedOutPrompt from '../components/SignedOutPrompt';
import { buildSavedSearchUrl, filtersFromSavedSearch, savedSearchLocationLabel } from '../lib/searchNavigation';

const SavedSearchesPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, isLoading: authLoading, openAuthModal } = useAuth();

    const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isClearing, setIsClearing] = useState(false);
    const [editingSearchId, setEditingSearchId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');

    // Tab title is set by <DocumentTitle> in this component's JSX.

    // Redirect to auth if not logged in
    useEffect(() => {
        if (!authLoading && !user) {
            openAuthModal({
                type: 'favourite',
                gymId: '',
                returnUrl: '/account/saved-searches'
            });
        }
    }, [authLoading, user, openAuthModal]);

    // Load saved searches
    useEffect(() => {
        if (!user) return;

        const loadData = async () => {
            try {
                const searches = await getSavedSearches(user.uid);
                setSavedSearches(searches);
            } catch (error) {
                console.error('Error loading saved searches:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [user]);

    const handleLoadSearch = (search: SavedSearch) => {
        // UI-01: the search's OWN stored state (stamped at save time). Legacy
        // documents carry no state and were always applied against
        // DEFAULT_STATE, so the fallback preserves them exactly.
        // Item 3 (operator-ruled 2026-08-14): this page used to write the params in
        // its OWN order (styles/age/exp/radius), so the same saved search produced a
        // different address here than when loaded from the directory. Both now call
        // buildSavedSearchUrl. The byte change is deliberate and was ruled explicitly;
        // nothing breaks, because the read side looks params up by NAME and these
        // multi-filter URLs are noindex.
        const fullUrl = buildSavedSearchUrl(search);

        // Item 3: the router-state payload was a third hand-written copy of the same
        // saved-search → FilterState mapping, character-identical to the one the URL
        // builder uses. Sharing it keeps the address and the carried filters derived
        // from ONE mapping, so they cannot describe different searches.
        navigate(fullUrl, {
            state: {
                filters: filtersFromSavedSearch(search),
                viewMode: search.viewMode
            }
        });
    };

    const handleDeleteSearch = async (searchId: string) => {
        if (!user) return;
        try {
            await deleteSavedSearch(user.uid, searchId);
            setSavedSearches(prev => prev.filter(s => s.id !== searchId));
        } catch (error) {
            console.error('Error deleting search:', error);
        }
    };

    const handleClearAll = async () => {
        if (!user) return;

        setIsClearing(true);
        try {
            await clearSavedSearches(user.uid);
            setSavedSearches([]);
        } catch (error) {
            console.error('Error clearing saved searches:', error);
        } finally {
            setIsClearing(false);
        }
    };



    const handleStartEdit = (search: SavedSearch) => {
        setEditingSearchId(search.id);
        setEditingName(search.name);
    };

    const handleSaveEdit = async () => {
        if (!editingSearchId || !editingName.trim() || !user) return;

        try {
            await updateSavedSearch(user.uid, editingSearchId, { name: editingName.trim() });
            setSavedSearches(prev => prev.map(s =>
                s.id === editingSearchId ? { ...s, name: editingName.trim() } : s
            ));
            setEditingSearchId(null);
            setEditingName('');
        } catch (error) {
            console.error('Error updating search:', error);
        }
    };

    const handleCancelEdit = () => {
        setEditingSearchId(null);
        setEditingName('');
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-canvas">
                <Loader2 className="h-16 w-16 text-brand-primary animate-spin" />
            </div>
        );
    }

    // Not signed in — invite them to sign in, at this address (item 4, 2026-08-14).
    // Previously this shared the spinner branch above via `authLoading || !user`,
    // so a signed-out visitor saw a spinner that could never resolve. It was
    // unreachable until ProtectedRoute stopped redirecting to '/'. The effect above
    // still opens the modal with returnUrl '/account/saved-searches'.
    if (!user) {
        return (
            <>
                {/* ⭐ UI-112 — see AccountPage's signed-out branch for the full note. The skip
                    link targeted `#main-content`, which did not exist here; measured live at
                    origin, /blog passing as the control. Wording and icon are operator-ruled
                    (2026-08-14) and untouched. */}
                <DocumentTitle title="Saved Searches – Martial Arts Network" />
                <main id="main-content" className="min-h-screen flex items-center justify-center bg-surface-canvas px-4">
                    <SignedOutPrompt
                        icon={Search}
                        heading="Sign in to view saved searches"
                        body="Create an account to save searches and come back to them."
                    />
                </main>
            </>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-surface-canvas">
            <DocumentTitle title="Saved Searches – Martial Arts Network" />
            <div className="h-header flex-shrink-0" />

            <main id="main-content" className="flex-1 py-8 px-4">
                <div className="max-w-xl mx-auto">
                    {/* Back button and title */}
                    <Link
                        to="/account"
                        state={{ tab: 'activity' }}
                        className="inline-flex items-center text-sm text-text-muted hover:text-brand-primary transition-colors mb-4"
                    >
                        <ArrowLeft size={16} className="mr-1.5" />
                        Back to Account
                    </Link>

                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-semibold text-text-primary">Saved searches</h1>

                        {savedSearches.length > 0 && (
                            /* UI-13 justified raw: link-style inline text action (no padding/height/
                               bg/border/weight of the Button base), and Button's disabled:opacity-60
                               would visibly dim the control during isClearing — a delta outside the
                               accepted set. Conversion = noise, not consistency. */
                            <button
                                onClick={handleClearAll}
                                disabled={isClearing}
                                className="flex items-center gap-2 text-sm text-text-muted hover:text-red-600 transition-colors"
                            >
                                {isClearing ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Trash2 size={14} />
                                )}
                                Clear all
                            </button>
                        )}
                    </div>

                    {isLoading ? (
                        // Spinner, not a skeleton: saved searches are a variable, typically-small
                        // list, so a fixed-count skeleton can't honestly predict the layout (raised
                        // bar — burden of proof on the skeleton). role=status keeps the announced
                        // loading state consistent with the skeleton regions elsewhere.
                        <div className="flex flex-col items-center justify-center py-16 text-center px-4" role="status" aria-label="Loading saved searches" aria-busy="true">
                            <Loader2 className="h-10 w-10 text-brand-primary animate-spin" />
                        </div>
                    ) : savedSearches.length === 0 ? (
                        <div className="bg-white rounded-xl border border-border-default p-8 text-center">
                            <p className="text-text-muted mb-4">No saved searches yet</p>
                            <Link
                                to="/gyms"
                                state={{ triggerFilters: true }}
                                className="inline-block px-4 py-2 bg-brand-primary text-white rounded-lg font-medium hover:bg-brand-primary-hover transition-colors"
                            >
                                Browse directory
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {savedSearches.map((search) => (
                                <div
                                    key={search.id}
                                    className="bg-white rounded-xl border border-border-default p-4"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            {editingSearchId === search.id ? (
                                                <div className="flex items-center gap-2 mb-2">
                                                    <input
                                                        type="text"
                                                        aria-label="Search name"
                                                        value={editingName}
                                                        onChange={(e) => setEditingName(e.target.value)}
                                                        className="flex-1 px-2 py-1 border border-border-default rounded text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-primary"
                                                        autoFocus
                                                    />
                                                    {/* UI-13: ghost/icon Button, faithful overrides — 26px micro-control
                                                        (p-1 + 18px glyph), no border/hover-surface, colour-only hover.
                                                        No aria-label existed here; behaviour is frozen so none is added
                                                        (IconButton would force one), hence Button rather than IconButton. */}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={handleSaveEdit}
                                                        className="min-h-0 min-w-0 border-0 p-1 text-green-600 hover:bg-transparent hover:text-green-700"
                                                    >
                                                        <Check size={18} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={handleCancelEdit}
                                                        className="min-h-0 min-w-0 border-0 p-1 text-text-subtle hover:bg-transparent hover:text-text-primary"
                                                    >
                                                        <X size={18} />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-medium text-text-primary truncate">{search.name}</h3>
                                                </div>
                                            )}

                                            {/* ⭐ THE SAME CANONICAL LINE AS THE DIRECTORY DROPDOWN (operator-ruled
                                                2026-08-17). This page previously showed the raw stored `location` and
                                                NO state at all, while the dropdown showed a state tag — the two
                                                surfaces disagreed about the same document. One shared helper is what
                                                stops them drifting again; see `savedSearchLocationLabel`. */}
                                            <div className="flex items-center gap-2 text-sm text-text-muted">
                                                <MapPin size={14} />
                                                <span>{savedSearchLocationLabel(search)}{search.useRadius ? ` • ${search.radius}km` : ''}</span>
                                            </div>

                                            {search.filters.styles.length > 0 && (
                                                <p className="text-sm text-text-muted mt-1">
                                                    {search.filters.styles.join(', ')}
                                                </p>
                                            )}

                                            {search.filters.ageGroups.length > 0 && (
                                                <p className="text-sm text-text-muted mt-1">
                                                    {search.filters.ageGroups.join(', ')}
                                                </p>
                                            )}

                                            {(search.filters.experienceLevels || []).length > 0 && (
                                                <p className="text-sm text-text-muted mt-1">
                                                    {(search.filters.experienceLevels || []).join(', ')}
                                                </p>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-1">
                                            {/* UI-13: IconButton ghost/dense, faithful overrides — 32px row
                                                actions (p-2 + 16px glyph), no border/hover-surface, colour-only
                                                hover. ariaLabel carries the pre-existing aria-label values. */}
                                            <IconButton
                                                icon={<Pencil size={16} />}
                                                ariaLabel="Rename search"
                                                variant="ghost"
                                                size="dense"
                                                onClick={() => handleStartEdit(search)}
                                                className="min-h-0 min-w-0 border-0 p-2 text-text-subtle hover:bg-transparent hover:text-text-primary"
                                                title="Rename"
                                            />
                                            <IconButton
                                                icon={<Trash2 size={16} />}
                                                ariaLabel="Delete search"
                                                variant="ghost"
                                                size="dense"
                                                onClick={() => handleDeleteSearch(search.id)}
                                                className="min-h-0 min-w-0 border-0 p-2 text-text-subtle hover:bg-transparent hover:text-red-600"
                                                title="Delete"
                                            />
                                        </div>
                                    </div>

                                    {/* UI-13 justified raw: no Button variant matches a borderless
                                        surface-canvas fill with hover:bg-border-default at inherited 16px
                                        text and rounded-lg; faithful conversion needs ~8 overrides
                                        nullifying variant bg/hover/border plus base radius/text-size and
                                        the size min-height. It also deliberately shares its chip styling
                                        (px-4 py-2 rounded-lg font-medium) with the "Browse directory"
                                        Link above — converting one side would fork that pair. */}
                                    <button
                                        onClick={() => handleLoadSearch(search)}
                                        className="w-full mt-3 px-4 py-2 bg-surface-canvas text-text-primary rounded-lg font-medium hover:bg-border-default transition-colors"
                                    >
                                        Load search
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

        </div>
    );
};

export default SavedSearchesPage;
